import { useCallback, useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { CuratedFeedRead, FeedSourceRead } from '../../types/api';
import { fetchFeedCatalog } from './newsApi';

/**
 * The curated catalogue: one hand-checked list of publications, the same for
 * every business.
 *
 * It is global and it does not change while the app is open, so the fetch is
 * cached in a module-level promise rather than re-run per mount — opening the
 * feed dialog twice is one request, not two. A failed load clears the cache so
 * the next open really retries; a successful one is kept for the session.
 */
let catalogPromise: Promise<CuratedFeedRead[]> | null = null;

function loadCatalog(): Promise<CuratedFeedRead[]> {
  if (catalogPromise === null) {
    catalogPromise = fetchFeedCatalog().catch((caught: unknown) => {
      catalogPromise = null;
      throw caught;
    });
  }
  return catalogPromise;
}

/** One industry's worth of the catalogue, in the order the server sent it. */
export interface CuratedFeedGroup {
  slug: string;
  label: string;
  feeds: CuratedFeedRead[];
}

export interface FeedCatalogResult {
  feeds: CuratedFeedRead[];
  /** The same list, grouped by industry, first-seen order preserved. */
  groups: CuratedFeedGroup[];
  isLoading: boolean;
  error: string | null;
  /** Drop the cached list and go out again. The error state's way back. */
  reload: () => void;
}

/** Stable empty results, so consumers do not see a new identity per render. */
const NO_FEEDS: never[] = [];
const NO_GROUPS: never[] = [];

/**
 * Groups the flat catalogue by industry.
 *
 * The server orders by industry and then by `sort_order`, so first-seen order
 * is the server's order: this only buckets, it never sorts. Doing it here keeps
 * one definition of what a group is, for both the list and the industry match.
 */
export function groupCatalog(feeds: readonly CuratedFeedRead[]): CuratedFeedGroup[] {
  const groups: CuratedFeedGroup[] = [];
  const bySlug = new Map<string, CuratedFeedGroup>();

  for (const feed of feeds) {
    const existing = bySlug.get(feed.industry_slug);
    if (existing) {
      existing.feeds.push(feed);
      continue;
    }
    const group: CuratedFeedGroup = {
      slug: feed.industry_slug,
      label: feed.industry_label,
      feeds: [feed],
    };
    bySlug.set(group.slug, group);
    groups.push(group);
  }

  return groups;
}

/** Words worth matching on: short ones ("and", "the") say nothing about a trade. */
function significantWords(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 4);
}

/**
 * The catalogue group that best answers the industry the user typed at
 * onboarding — which is free text, not one of these slugs.
 *
 * Deliberately loose and deliberately cheap: whole-label containment either
 * way first, then a significant word in common. It only decides which group is
 * open when the tab is first shown, so a miss costs a click and a false match
 * costs nothing at all. No match returns null and the first group opens.
 */
export function matchIndustryGroup(
  groups: readonly CuratedFeedGroup[],
  industry: string | null,
): string | null {
  const needle = industry?.trim().toLowerCase() ?? '';
  if (!needle) return null;

  for (const group of groups) {
    const label = group.label.toLowerCase();
    if (needle.includes(label) || label.includes(needle)) return group.slug;
  }

  for (const group of groups) {
    if (significantWords(group.label).some((word) => needle.includes(word))) return group.slug;
  }

  const needleWords = significantWords(needle);
  for (const group of groups) {
    const label = group.label.toLowerCase();
    if (needleWords.some((word) => label.includes(word))) return group.slug;
  }

  return null;
}

/**
 * True when this business already follows a catalogue entry.
 *
 * `curated_id` is the honest answer and is checked first; the feed URL is the
 * fallback, for a row that was pasted in by hand before the catalogue offered
 * the same publication. Compared case-insensitively without a trailing slash,
 * because those two are the same address and neither the user nor the feed's
 * publisher is consistent about them.
 */
export function followedCatalogIds(sources: readonly FeedSourceRead[]): {
  ids: ReadonlySet<string>;
  urls: ReadonlySet<string>;
} {
  const ids = new Set<string>();
  const urls = new Set<string>();
  for (const source of sources) {
    if (source.curated_id) ids.add(source.curated_id);
    urls.add(normaliseFeedUrl(source.feed_url));
  }
  return { ids, urls };
}

/** One spelling of a feed address, for comparison only — never for a request. */
export function normaliseFeedUrl(value: string): string {
  return value.trim().toLowerCase().replace(/\/+$/, '');
}

/**
 * The curated catalogue, loaded once per session.
 *
 * Shape follows `useSlideThemes`: one effect, no synchronous state write, and a
 * failure is reported rather than thrown — the catalogue is one of three ways
 * to add a source, so the other two stay usable when it cannot be read.
 */
export function useFeedCatalog(): FeedCatalogResult {
  const [feeds, setFeeds] = useState<CuratedFeedRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const loaded = await loadCatalog();
        if (cancelled) return;
        setFeeds(loaded);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setFeeds(null);
        setError(getErrorMessage(caught, 'Could not load our list of publications.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const reload = useCallback(() => {
    catalogPromise = null;
    setNonce((value) => value + 1);
  }, []);

  // Memoised because the grouped list is what the panel's own derivations key
  // on: a fresh array every render would rebuild all of them for nothing.
  const groups = useMemo(() => (feeds === null ? NO_GROUPS : groupCatalog(feeds)), [feeds]);

  return {
    feeds: feeds ?? NO_FEEDS,
    groups,
    isLoading: feeds === null && error === null,
    error,
    reload,
  };
}
